import clsx from 'clsx'
import { createPortal } from 'react-dom'
import { SyntheticEvent, useCallback, useState, HTMLAttributes, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Backdrop,
  Autocomplete,
  AutocompleteHighlightChangeReason,
  Theme,
  useMediaQuery,
} from '@mui/material'
import { useArticlesSearchProvider } from '@obeta/data/lib/hooks/useArticleSearchProvider'
import { useHistory, useLocation } from '@obeta/data/lib/hooks/useHistoryApi'
import { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete/Autocomplete'

import { AutocompleteGroup, AutocompleteOption, SearchInput } from '.'

import { useMobileScrollHandler } from '@obeta/data/lib/hooks/useMobileScrollHandler'
import coreVariables from '@obeta/assets/theme/coreVariablesV2.module.scss'
import { Category } from '@obeta/models/lib/models/Category'
import { useSearchArticles } from '@obeta/data/lib/hooks/useSearchArticles'
import { useBarcodeScanner } from '@obeta/data/lib/hooks/useBarcodeScanner'
import { useRemToPx } from '@obeta/data/lib/hooks/useRemToPixel'
import { useKeyboardHeight } from '@obeta/data/lib/hooks/useKeyboardHeight'
import { useHeaderDimensions } from '@obeta/data/lib/hooks/useHeaderDimensions'
import { ArticleSearchArea } from '@obeta/models/lib/models/Search'
import { TertiaryIconButton } from '../../../custom-button/CustomButton'
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material/useAutocomplete/useAutocomplete'
import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete/Autocomplete'
import { ShopRoutes } from '@obeta/utils/lib/variables'
import styles from './SearchBar.module.scss'
import { CloseIcon, ScanIcon } from '../../../icons'
import { useSuggestionOption } from './useSuggestionOption'

import {
  useSearchAutocompleteView,
  ESuggestionGroup,
  SuggestionItem,
  ESuggestionType,
} from '@obeta/data/lib/hooks'

export interface SearchBarProps {
  placeholder?: string
  showBarCodeScanner?: boolean
  withClear?: boolean
}

export const SearchBar = (props: SearchBarProps) => {
  const { showBarCodeScanner = false, withClear = true, placeholder } = props

  const searchInputContainerRef = useRef<HTMLDivElement>(null)

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.between('xs', 'md'))
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()

  const [showSearchModal, setShowSearchModal] = useState(false)
  const close = useCallback(() => setShowSearchModal(false), [])
  const show = useCallback(() => setShowSearchModal(true), [])
  const { startSearchScan } = useBarcodeScanner()
  const { changeSearchParams, searchParams } = useArticlesSearchProvider()

  const alterSearchParamsAndClose = useCallback(
    (
      paramName: 'searchString' | 'obetaCategory' | 'suppliers',
      paramValue: string | Category | Record<string, { id: string; name: string }>,
      cleanSearch = false
    ) => {
      let newSearchParams = {}
      if (!cleanSearch && searchParams) {
        newSearchParams = Object.assign(newSearchParams, searchParams)
      }
      newSearchParams[paramName] = paramValue

      const replaceRoute = location.pathname.startsWith('/search')

      changeSearchParams(
        {
          searchParams: {
            articleSearchArea: ArticleSearchArea.IMMEDIATELY_AVAILABLE,
            trigger: 'searchbar',
            ...newSearchParams,
          },
          route: replaceRoute ? 'replace' : 'push',
        },
        'searchbar'
      )
      close()
    },
    [changeSearchParams, close, location.pathname, searchParams]
  )

  /**
   * Always start new search when searchString has changed
   */
  const onSearchConfirmed = useCallback(
    (term) => alterSearchParamsAndClose('searchString', term, true),
    [alterSearchParamsAndClose]
  )

  const searchArticlesProps = useSearchArticles(searchParams, onSearchConfirmed)

  const [highlightedOption, setHighlighted] = useState<SuggestionItem | null>(null)
  const autocompleteOptions = useSearchAutocompleteView(searchArticlesProps.searchText)

  const [searchInputRect, setSearchInputRect] = useState<DOMRect | null>(null)

  const suggestionOption = useSuggestionOption({
    query: searchArticlesProps.searchText,
    highlightedOption,
    autocompleteOptions,
  })

  const [searchInputValue, setSearchInputValue] = useState<SuggestionItem>({
    title: searchArticlesProps.searchText,
    type: ESuggestionType.Suggestion,
    group: ESuggestionGroup.Suggestion,
  })

  const mobileHeaderHeight = useRemToPx(parseFloat(coreVariables.mobileHeaderHeight))

  const isTopSectionVisible = useMobileScrollHandler({
    targetSectionHeight: mobileHeaderHeight,
    isEnabled: isMobile,
  })

  const keyboardHeight = useKeyboardHeight()
  const {
    dimensions: { height: headerHeight },
  } = useHeaderDimensions()

  const headerElement = document.getElementById('app-header')

  const handleSearchDropdownOpen = () => {
    const rect = searchInputContainerRef.current?.getBoundingClientRect()
    if (rect) setSearchInputRect(rect)
    show()
  }
  const handleSearchDropdownClose = () => {
    close()
    setHighlighted(null)
  }

  const handleAutocompleteChange = (_event, value, reason: AutocompleteChangeReason) => {
    if (reason === 'selectOption') {
      if (value?.type === ESuggestionType.Store) {
        const query = new URLSearchParams({ store: value.id })
        history.push({ pathname: ShopRoutes.Stores, search: `?${query}` })
        searchArticlesProps.handleSetSearchValue('')
        return
      } else if (value?.type === ESuggestionType.Supplier) {
        alterSearchParamsAndClose(
          'suppliers',
          { [value.filterName]: { id: value.filterName, name: value.filterName } },
          true
        )
        handleSearchDropdownClose()
      } else if (value?.type === ESuggestionType.Category) {
        alterSearchParamsAndClose(
          'obetaCategory',
          { name: value.title, id: value.id, level: 0, rootId: value.id },
          true
        )
        handleSearchDropdownClose()
      } else {
        searchArticlesProps.handleSetSearchValue(value?.title || '')
        handleSubmit()
      }
      setSearchInputValue(value)
    }
    if (reason === 'createOption') {
      searchArticlesProps.handleSetSearchValue(value)
      handleSubmit()
    }
  }

  const handleHighlight = (
    _event: SyntheticEvent<Element, Event>,
    option: SuggestionItem | null,
    reason: AutocompleteHighlightChangeReason
  ) => {
    if (reason === 'keyboard') {
      setHighlighted(option)
    }
  }

  const handleInputChange = (
    _event: SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'reset' && !value) return

    searchArticlesProps.handleSetSearchValue(value)
  }

  const handleBarCodeClick = (e) => {
    e.stopPropagation()
    startSearchScan()
  }

  const handleSubmit = () => {
    searchArticlesProps.handleSubmit()
    handleSearchDropdownClose()
  }

  const mobilePopperStyles = clsx(styles['autocomplete-popper-mobile'], {
    [styles['autocomplete-popper-mobile--scrolled']]: !isTopSectionVisible,
  })

  const autocompleteClasses = {
    root: styles['autocomplete-root'],
    input: styles['autocomplete-input'],
    listbox: styles['autocomplete-listbox'],
    option: styles['search-suggestion-item'],
    paper: styles['search-suggestion-paper'],
    popper: isMobile ? mobilePopperStyles : styles['autocomplete-popper'],
  }

  const headerWithSafeAreaHeight = `calc(${headerHeight}px + var(--obeta-safe-area-top-root))`

  const listboxProps = {
    style: {
      maxHeight: `calc(100vh - ${headerWithSafeAreaHeight} - ${keyboardHeight}px )`,
    },
  }

  const popperProps = isMobile
    ? { anchorEl: headerElement }
    : {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 5], // [horizontal, vertical]
            },
          },
        ],
      }

  const showClear = withClear && !!searchArticlesProps.searchText

  const showEndAdornment = showClear || showBarCodeScanner

  const onClearSearch = () => {
    searchArticlesProps.handleSetSearchValue('')
  }

  const endAdornment = showEndAdornment && (
    <div className={styles.endAdornmentWrapper}>
      {showClear && (
        <TertiaryIconButton icon={<CloseIcon />} size="small" onClick={onClearSearch} />
      )}
      {showBarCodeScanner && (
        <div className={styles.scannerIcon} onClick={handleBarCodeClick}>
          <ScanIcon width={24} height={24} />
        </div>
      )}
    </div>
  )

  const renderInput = (params: AutocompleteRenderInputParams) => {
    const handleSearchTab = () => {
      const nextOption = highlightedOption || suggestionOption
      if (nextOption) {
        searchArticlesProps.handleSetSearchValue(nextOption.title)
      }
    }

    const handleSearchEnter = () => {
      if (!highlightedOption) {
        handleSubmit()
      }
    }

    const searchInputProps = {
      params: params,
      placeholder: placeholder,
      suggestion: showSearchModal ? suggestionOption?.title : '',
      showEndAdornment: showEndAdornment,
      endAdornment: endAdornment,
      onSearch: handleSubmit,
      onSearchTab: handleSearchTab,
      onSearchEnter: handleSearchEnter,
      onFocus: handleSearchDropdownOpen,
      close: handleSearchDropdownClose,
      autoFocus: true,
    }

    const onBackdropClick = (e) => {
      const target = e.target as HTMLElement
      const searchWrapper = document.getElementById('searchInputWrapper')
      if (searchWrapper?.contains(target)) return
      // Prevent closing the dropdown when clicking inside the search input or suggestions
      if (['search-suggestions', 'searchInputWrapper'].includes(target.id)) return
      handleSearchDropdownClose()
    }

    if (isMobile) {
      return (
        <>
          {createPortal(
            <Backdrop sx={{ zIndex: 999 }} open={showSearchModal} onClick={onBackdropClick} />,
            document.body
          )}
          <SearchInput {...searchInputProps} />
        </>
      )
    }

    // For non-mobile viewports, we render the search input inside a portal if the modal is open
    // and the search input container has a valid bounding rectangle.
    // This allows the search input to be positioned correctly relative to the search bar
    // and enables the backdrop to cover the entire viewport, but still allows the search input to be shown above the backdrop.
    return showSearchModal && searchInputRect ? (
      createPortal(
        <Backdrop sx={{ zIndex: 1299 }} open={showSearchModal} onClick={onBackdropClick}>
          {searchInputRect && (
            <div
              style={{
                position: 'fixed',
                top: searchInputRect.top,
                left: searchInputRect.left,
                width: searchInputRect.width,
                height: searchInputRect.height,
                zIndex: 1300,
              }}
            >
              <SearchInput {...searchInputProps} />
            </div>
          )}
        </Backdrop>,
        document.body
      )
    ) : (
      <div ref={searchInputContainerRef}>
        <SearchInput {...searchInputProps} />
      </div>
    )
  }

  const renderGroup = (group: AutocompleteRenderGroupParams) => (
    <AutocompleteGroup
      key={`${group.group}-${group.key}`}
      group={group}
      onClose={handleSearchDropdownClose}
    />
  )

  const renderOption = (
    props: HTMLAttributes<HTMLLIElement> & {
      key: string
    },
    option: SuggestionItem,
    { inputValue }
  ) => {
    const isSelected =
      option.title === highlightedOption?.title && option.type === highlightedOption?.type

    const selectHandler = (option: SuggestionItem) => {
      setSearchInputValue(option)
    }

    return (
      <AutocompleteOption
        key={`${option.title}-${option.type}`}
        inputValue={inputValue}
        option={option}
        optionProps={props}
        selected={isSelected}
        onSelect={selectHandler}
      />
    )
  }

  return (
    <div className={styles.searchContainer}>
      <Autocomplete
        id="search-suggestions"
        classes={autocompleteClasses}
        sx={{ height: '100%', width: '100%' }}
        options={autocompleteOptions}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
        renderGroup={renderGroup}
        renderInput={renderInput}
        renderOption={renderOption}
        filterOptions={(options) => options}
        onChange={handleAutocompleteChange}
        onInputChange={handleInputChange}
        onOpen={handleSearchDropdownOpen}
        onClose={(e, reason) => {
          const target = e.target as HTMLElement
          const searchWrapper = document.getElementById('searchInputWrapper')
          // Prevent closing the dropdown when clicking inside the search input or suggestions
          if (searchWrapper?.contains(target)) return
          if (['search-suggestions', 'searchInputWrapper'].includes(target.id)) return
          // Backdrop handles closing the dropdown
          if (reason === 'blur') return
          handleSearchDropdownClose()
        }}
        onHighlightChange={handleHighlight}
        value={searchInputValue}
        inputValue={searchArticlesProps.searchText}
        open={showSearchModal}
        forcePopupIcon={false}
        noOptionsText={t('HEADER.NO_RESULT')}
        blurOnSelect
        freeSolo={true}
        slotProps={{ listbox: listboxProps, popper: popperProps }}
      />
    </div>
  )
}
